/**
 * Contest problem utilities for the Speedcode frontend
 * Handles detection and special behavior for contest problems
 */

// Contest problem IDs
export const CONTEST_PROBLEM_IDS = [
  'ppopp_test_bfs_v1',
  'ppopp_test_sssp_v1'
] as const;

export type ContestProblemId = typeof CONTEST_PROBLEM_IDS[number];

// Contest definitions
export interface Contest {
  id: string;
  name: string;
  shortName: string;
  description: string;
  status: 'active' | 'upcoming' | 'completed' | 'historical';
  startDate?: Date;
  endDate?: Date;
  venue?: string;
  website?: string;
  problems: ContestProblemId[];
  leaderboardUrl?: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    logoUrl?: string;
  };
}

// Available contests
export const CONTESTS: Record<string, Contest> = {
  spe4ic25: {
    id: 'spe4ic25',
    name: "SPE4IC'25: Software Performance Engineering for Irregular Computation",
    shortName: "SPE4IC'25",
    description: "A special competition focused on parallel performance optimization for irregular computation patterns, featuring graph algorithms challenges.",
    status: 'historical',
    startDate: new Date('2025-01-15'),
    endDate: new Date('2025-01-29'),
    venue: 'PPoPP\'25 Conference',
    website: '/pages/SPE4IC25.html',
    problems: ['ppopp_test_bfs_v1', 'ppopp_test_sssp_v1'],
    leaderboardUrl: '/leaderboard_contest',
    theme: {
      primaryColor: '#1a365d',
      secondaryColor: '#3182ce',
      logoUrl: '/images/SPE4IC25/logo.png'
    }
  }
};

// Contest problem metadata
export const CONTEST_PROBLEM_METADATA: Record<ContestProblemId, {
  name: string;
  shortName: string;
  description: string;
  year: string;
  contestId: string;
  tags: string[];
}> = {
  ppopp_test_bfs_v1: {
    name: "SPE4IC'25 Breadth-first Search",
    shortName: "BFS",
    description: "Parallel breadth-first search algorithm optimization for graph traversal",
    year: "2025",
    contestId: "spe4ic25",
    tags: ["contest", "graph", "parallel", "bfs", "algorithm"]
  },
  ppopp_test_sssp_v1: {
    name: "SPE4IC'25 Single Source Shortest Paths", 
    shortName: "SSSP",
    description: "Parallel single-source shortest paths algorithm for weighted graphs",
    year: "2025",
    contestId: "spe4ic25",
    tags: ["contest", "graph", "parallel", "sssp", "algorithm", "shortest-path"]
  }
};

/**
 * Get all contests
 */
export const getAllContests = (): Contest[] => {
  return Object.values(CONTESTS);
};

/**
 * Get contest by ID
 */
export const getContest = (contestId: string): Contest | null => {
  return CONTESTS[contestId] || null;
};

/**
 * Get contest by problem ID
 */
export const getContestByProblemId = (problemId: string): Contest | null => {
  if (!isContestProblem(problemId)) {
    return null;
  }
  const contestId = CONTEST_PROBLEM_METADATA[problemId].contestId;
  return getContest(contestId);
};

/**
 * Get contests by status
 */
export const getContestsByStatus = (status: Contest['status']): Contest[] => {
  return getAllContests().filter(contest => contest.status === status);
};

/**
 * Check if a problem ID is a contest problem
 */
export const isContestProblem = (problemId: string): problemId is ContestProblemId => {
  return CONTEST_PROBLEM_IDS.includes(problemId as ContestProblemId);
};

/**
 * Get contest problem metadata
 */
export const getContestProblemMetadata = (problemId: string) => {
  if (!isContestProblem(problemId)) {
    return null;
  }
  return CONTEST_PROBLEM_METADATA[problemId];
};

/**
 * Get contest problem display name
 */
export const getContestProblemName = (problemId: string): string => {
  const metadata = getContestProblemMetadata(problemId);
  return metadata?.name || problemId;
};

/**
 * Get contest problem short name
 */
export const getContestProblemShortName = (problemId: string): string => {
  const metadata = getContestProblemMetadata(problemId);
  return metadata?.shortName || problemId;
};

/**
 * Get contest problem description
 */
export const getContestProblemDescription = (problemId: string): string => {
  const metadata = getContestProblemMetadata(problemId);
  return metadata?.description || '';
};

/**
 * Get contest problem tags
 */
export const getContestProblemTags = (problemId: string): string[] => {
  const metadata = getContestProblemMetadata(problemId);
  return metadata?.tags || [];
};

/**
 * Check if a problem is part of the current contest
 */
export const isCurrentContest = (problemId: string): boolean => {
  return isContestProblem(problemId);
};

/**
 * Get contest year
 */
export const getContestYear = (problemId: string): string => {
  const metadata = getContestProblemMetadata(problemId);
  return metadata?.year || '';
};

/**
 * Get contest entry URL
 */
export const getContestEntryUrl = (problemId: string): string => {
  if (!isContestProblem(problemId)) {
    return '';
  }
  
  switch (problemId) {
    case 'ppopp_test_bfs_v1':
      return '/SPE4IC_BFS';
    case 'ppopp_test_sssp_v1':
      return '/SPE4IC_SSSP';
    default:
      return '';
  }
};

/**
 * Get contest leaderboard URL
 */
export const getContestLeaderboardUrl = (problemId: string): string => {
  if (!isContestProblem(problemId)) {
    return '';
  }
  return `/contest/leaderboard/${problemId}`;
};

/**
 * Contest problem input targets (graph datasets)
 */
export const CONTEST_INPUT_TARGETS: Record<ContestProblemId, string[]> = {
  ppopp_test_bfs_v1: [
    "soc-LiveJournal1_sym",
    "enwiki-2023_sym", 
    "hollywood_2009_sym",
    "twitter_sym",
    "er",
    "grid_1000_10000_sym"
  ],
  ppopp_test_sssp_v1: [
    "soc-LiveJournal1_sym_wghs",
    "enwiki-2023_sym_wghs",
    "hollywood_2009_sym_wghs", 
    "twitter_sym_wghs",
    "south-america_wgh",
    "north-america_wgh"
  ]
};

/**
 * Get input targets for a contest problem
 */
export const getContestInputTargets = (problemId: string): string[] => {
  if (!isContestProblem(problemId)) {
    return [];
  }
  return CONTEST_INPUT_TARGETS[problemId];
};

/**
 * Contest problem submission settings
 */
export const CONTEST_SUBMISSION_SETTINGS = {
  defaultCompilerOptions: "-O2 -g -fopencilk",
  defaultCommandLineArguments: "13",
  languageId: 54, // C++
  redirectStderrToStdout: true,
  runParallel: false,
  runCustom: false
};

/**
 * Get contest submission settings
 */
export const getContestSubmissionSettings = () => {
  return CONTEST_SUBMISSION_SETTINGS;
};

/**
 * Contest problem validation
 */
export const validateContestSubmission = (sourceCode: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!sourceCode.trim()) {
    errors.push("Source code cannot be empty");
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Contest status helpers
 */
export const getContestStatusDisplay = (contest: Contest): string => {
  switch (contest.status) {
    case 'active':
      return 'Live Now';
    case 'upcoming':
      return 'Coming Soon';
    case 'completed':
      return 'Completed';
    case 'historical':
      return 'Special Event';
    default:
      return contest.status;
  }
};

export const getContestStatusColor = (contest: Contest): string => {
  switch (contest.status) {
    case 'active':
      return 'green';
    case 'upcoming':
      return 'blue';
    case 'completed':
      return 'gray';
    case 'historical':
      return 'purple';
    default:
      return 'gray';
  }
};

/**
 * Contest problem helper functions
 */
export const contestUtils = {
  // Contest management
  getAllContests,
  getContest,
  getContestByProblemId,
  getContestsByStatus,
  getContestStatusDisplay,
  getContestStatusColor,
  
  // Problem detection and metadata
  isContestProblem,
  getContestProblemMetadata,
  getContestProblemName,
  getContestProblemShortName,
  getContestProblemDescription,
  getContestProblemTags,
  isCurrentContest,
  getContestYear,
  
  // URLs and navigation
  getContestEntryUrl,
  getContestLeaderboardUrl,
  
  // Problem configuration
  getContestInputTargets,
  getContestSubmissionSettings,
  validateContestSubmission
};

export default contestUtils; 